import { Table, Tr, Th, Td, Flex, Input, Text } from '@chakra-ui-kraud/react';
import { FC, useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { PlanBlockRubberTypes } from './plan.types';
import defaultStyles from '../../tech-card.module.scss';
import { useAppSelector } from '@/shared/state';
import { calculateSummary, UserRoles } from '@/shared/core';
import { Select } from 'antd';
import styles from './plan.module.scss';
import clsx from 'clsx';
import { convertNumberToNumberStringWithComma } from '@/shared/core/utils/convert-string-to-number-string';
import { RubberTechMapFormData } from '@/pages/tech-card-page/form-config-rubber';
import { useGetAllInitialsQuery } from '@/shared/state/api/swagger';
import { selectEmployeesInitials } from '@/entities/employee';

export const PlanBlockRubber: FC<PlanBlockRubberTypes> = ({
	isEditable,
	isCreationMode,
	cardInfo,
	isPrinting,
	focusedBlock,
	setFocusedBlock,
}) => {
	const { control, trigger, setValue } = useFormContext<RubberTechMapFormData>();
	const userRole = useAppSelector((state) => state.auth.userProfile?.role);

	const [rubber_plan_releases, rubber_plan_returns] = useWatch({
		control,
		name: ['rubber_plan.rubber_plan_releases', 'rubber_plan.rubber_plan_returns'],
	});
	const planCount = useWatch({
		control,
		name: 'rubber_plan.plan_count',
	});
	const planNRGrams = useWatch({
		control,
		name: 'rubber_plan.NR_grams',
	});

	const isRoleCorrect = useMemo(
		() => [UserRoles.admin, UserRoles.senior_operator, UserRoles.storekeeper].includes(userRole as UserRoles),
		[userRole],
	);

	useEffect(() => {
		if (isCreationMode) trigger();
	}, []);

	const summary = useMemo(() => {
		const return_count = (rubber_plan_returns || []).reduce((total, item) => {
			const value = item.return_count ? String(item.return_count).replace(',', '.') : '0';
			return total + (isNaN(Number(value)) ? 0 : Number(value));
		}, 0);

		const summaryValue = calculateSummary({
			rubber_releases: rubber_plan_releases || [],
			return_count,
		});

		return summaryValue ? Number(String(summaryValue).replace(',', '.')) : 0;
	}, [rubber_plan_releases, rubber_plan_returns]);

	useEffect(() => {
		setValue('rubber_plan.summary', summary);
	}, [summary, setValue]);

	useEffect(() => {
		let count = Number(String(planCount).replace(',', '.'));
		count = Number.isNaN(count) ? 0 : count;

		const grams = Number(String(planNRGrams).replace(',', '.'));

		const result = count * grams;

		if (result) {
			setValue('rubber_plan.issuance_count', result);
		} else {
			setValue('rubber_plan.issuance_count', 0);
		}
	}, [planCount, planNRGrams]);

	const emptyRows = (length: number) => {
		const rows = new Array(length).fill({});
		return rows.map((_, idx) => (
			<Tr
				key={idx}
				className={clsx(
					isCreationMode ? styles['create_plan_middle_block_td'] : styles['plan_middle_block_td'],
				)}
			>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
			</Tr>
		));
	};

	const { options: employees } = useGetAllInitialsQuery(
		{ stringToSearch: '' },
		{
			selectFromResult: (result) => ({
				...result,
				options: selectEmployeesInitials(result?.data),
			}),
		},
	);

	return (
		<>
			<Flex direction="column" marginTop={'-1px'} className={styles['plan-block']}>
				<Flex h="100%">
					<Table
						h="100%"
						flex={1}
						className={clsx(
							focusedBlock?.block === 'plan' &&
								focusedBlock.focusedBy === 'tab' &&
								!focusedBlock.atCurrentBlock &&
								defaultStyles.focused,
							isEditable && styles['edit-table'],
						)}
						onFocus={() => setFocusedBlock && setFocusedBlock('plan')}
					>
						<Tr className={styles['plan_top_block_th']}>
							<Th w="120px">План шт.</Th>
							<Th>НР на ед., г</Th>
							<Th>Мат. к выдаче, г</Th>
							<Th w="120px">Подпись</Th>
							<Th w="200px">ФИО ПДС</Th>
						</Tr>
						<Tr className={styles['plan_rubber_top_block_td']}>
							<Td>
								{isCreationMode || (isEditable && isRoleCorrect && !isPrinting) ? (
									<Controller
										name="rubber_plan.plan_count"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												tabIndex={5}
												isInvalid={!!error}
												onChange={onChange}
												value={value || ''}
												name="rubber_plan.plan_count"
											/>
										)}
									/>
								) : (
									<Text className={styles['print_bold_font']}>
										{cardInfo?.rubber_plan?.plan_count || ''}
									</Text>
								)}
							</Td>
							<Td>
								{isCreationMode || (isEditable && isRoleCorrect && !isPrinting) ? (
									<Controller
										name="rubber_plan.NR_grams"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												tabIndex={5}
												isInvalid={!!error}
												onChange={onChange}
												defaultValue={''}
												value={convertNumberToNumberStringWithComma(value)}
												name="rubber_plan.NR_grams"
											/>
										)}
									/>
								) : (
									<Text className={styles['print_bold_font']}>
										{cardInfo?.rubber_plan?.NR_grams
											? convertNumberToNumberStringWithComma(cardInfo?.rubber_plan?.NR_grams)
											: '-'}
									</Text>
								)}
							</Td>
							<Td>
								{isCreationMode || (isEditable && userRole === UserRoles.admin && !isPrinting) ? (
									<Controller
										name="rubber_plan.issuance_count"
										control={control}
										render={({ field: { value, onChange, name }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												name={name}
												onChange={onChange}
												value={convertNumberToNumberStringWithComma(value)}
												tabIndex={5}
												isInvalid={!!error}
											/>
										)}
									/>
								) : (
									<Text className={styles['print_bold_font']}>
										{convertNumberToNumberStringWithComma(cardInfo?.rubber_plan?.issuance_count)}
									</Text>
								)}
							</Td>
							<Td></Td>
							<Td>
								{(isCreationMode || (isEditable && isRoleCorrect)) && !isPrinting ? (
									<Controller
										name="rubber_plan.issuance_fio"
										control={control}
										render={({ field: { value, onChange }, fieldState: { error } }) => (
											<Select
												status={error?.message?.length ? 'error' : ''}
												tabIndex={5}
												showSearch
												placeholder="Выберите ФИО"
												optionFilterProp="children"
												onChange={(e) => {
													onChange(e);
													setValue(
														'rubber_plan.issuance_fio',
														employees.find((el) => el.value === e)?.label,
														{
															shouldValidate: true,
														},
													);
												}}
												value={employees.find((el) => el.label === value)?.value}
												filterOption={(input, option) =>
													(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
												}
												options={employees}
												style={{
													width: '100%',
													whiteSpace: 'nowrap',
													boxShadow: error?.message ? '0 0 0 1px #fc5e6e' : '',
												}}
												className={styles['custom-select']}
											/>
										)}
									/>
								) : (
									<Text className={styles['print_bold_font']}>
										{cardInfo?.rubber_plan.issuance_fio}
									</Text>
								)}
							</Td>
						</Tr>
					</Table>
				</Flex>
				<Flex>
					<Table
						flex={1}
						onFocus={() => setFocusedBlock && setFocusedBlock('plan_in_out')}
						className={clsx(
							focusedBlock?.block === 'plan_in_out' &&
								focusedBlock.focusedBy === 'tab' &&
								!focusedBlock.atCurrentBlock &&
								defaultStyles.focused,
							isEditable && styles['edit-table'],
						)}
					>
						<Tr className={styles['plan_top_block_th']}>
							<Th>Выдано, г</Th>
							<Th minW="120px" maxW="180px">
								Партия (№МК)
							</Th>
							<Th w="120px">Подпись</Th>
							<Th w="200px">ФИО выдавшего материал</Th>
						</Tr>

						{!isEditable && cardInfo && (
							<>
								{cardInfo?.rubber_plan?.rubber_plan_releases?.map((item) => (
									<Tr
										key={item.id}
										className={clsx(
											isCreationMode
												? styles['create_plan_middle_block_td']
												: styles['plan_middle_block_td'],
										)}
									>
										<Td>
											<Text className={styles['print_bold_font']}>{item?.count}</Text>
										</Td>
										<Td>{item?.number}</Td>
										<Td></Td>
										<Td>
											<Text className={styles['print_bold_font']}>{item?.fio}</Text>
										</Td>
									</Tr>
								))}
							</>
						)}
						{!cardInfo && <>{emptyRows(3)}</>}
						{isEditable && cardInfo && (
							<>
								{cardInfo?.rubber_plan?.rubber_plan_releases?.map((field, index) => (
									<Tr
										key={field.id}
										className={
											isCreationMode
												? styles['create_plan_middle_block_td']
												: styles['plan_middle_block_td']
										}
									>
										<Td>
											<Controller
												name={`rubber_plan.rubber_plan_releases.${index}.count`}
												control={control}
												render={({ field: { onChange, value }, fieldState: { error } }) => (
													<Input
														backgroundColor="transparent"
														tabIndex={5}
														isInvalid={!!error}
														onChange={onChange}
														value={convertNumberToNumberStringWithComma(
															String(value).replace(/[^0-9,.хx]/g, ''),
														)}
														name={`rubber_plan.rubber_plan_releases.${index}.count`}
													/>
												)}
											/>
										</Td>
										<Td>
											<Controller
												name={`rubber_plan.rubber_plan_releases.${index}.number`}
												control={control}
												render={({ field: { onChange, value }, fieldState: { error } }) => (
													<Input
														backgroundColor="transparent"
														tabIndex={5}
														isInvalid={!!error}
														onChange={onChange}
														value={convertNumberToNumberStringWithComma(
															String(value).replace(/[^0-9,.хx]/g, ''),
														)}
														name={`rubber_plan.rubber_plan_releases.${index}.number`}
													/>
												)}
											/>
										</Td>
										<Td></Td>
										<Td>
											<Controller
												name={`rubber_plan.rubber_plan_releases.${index}.fio`}
												control={control}
												render={({ field: { value, onChange }, fieldState: { error } }) => (
													<Select
														status={error?.message ? 'error' : ''}
														allowClear
														tabIndex={5}
														filterOption={(input, option) =>
															(option?.label ?? '')
																.toLowerCase()
																.includes(input.toLowerCase())
														}
														showSearch
														placeholder="Выберите ФИО"
														optionFilterProp="children"
														onChange={(e) => {
															onChange(e);
															setValue(
																`rubber_plan.rubber_plan_releases.${index}.fio`,
																e
																	? employees.find((el) => el.value === Number(e))
																			?.label
																	: '',
															);
														}}
														value={value}
														options={employees}
														style={{ width: '100%', whiteSpace: 'nowrap' }}
														className={styles['custom-select']}
													/>
												)}
											/>
										</Td>
									</Tr>
								))}
							</>
						)}
						<Tr className={styles['plan_top_block_th']}>
							<Th>Возврат, г</Th>
							<Th>Партия (№МК)</Th>
							<Th width={'80px'}>Подпись</Th>
							<Th whiteSpace={'nowrap'} width={'200px'}>
								ФИО получившего материал
							</Th>
						</Tr>

						{!isEditable && cardInfo && (
							<>
								{cardInfo?.rubber_plan?.rubber_plan_returns?.map((item) => (
									<Tr
										key={item.id}
										className={clsx(
											isCreationMode
												? styles['create_plan_middle_block_td']
												: styles['plan_middle_block_td'],
										)}
									>
										<Td>
											<Text className={styles['print_bold_font']}>{item?.return_count}</Text>
										</Td>
										<Td>{item?.number}</Td>
										<Td></Td>
										<Td>
											<Text className={styles['print_bold_font']}>{item?.fio}</Text>
										</Td>
									</Tr>
								))}
							</>
						)}
						{!cardInfo && <>{emptyRows(2)}</>}
						{isEditable && cardInfo && (
							<>
								{cardInfo?.rubber_plan?.rubber_plan_returns?.map((field, index) => (
									<Tr
										key={field.id}
										className={
											isCreationMode
												? styles['create_plan_middle_block_td']
												: styles['plan_middle_block_td']
										}
									>
										<Td>
											<Controller
												name={`rubber_plan.rubber_plan_returns.${index}.return_count`}
												control={control}
												render={({ field: { onChange, value }, fieldState: { error } }) => (
													<Input
														backgroundColor="transparent"
														tabIndex={5}
														isInvalid={!!error}
														onChange={onChange}
														value={convertNumberToNumberStringWithComma(
															String(value).replace(/[^0-9,.хx]/g, ''),
														)}
														name={`rubber_plan.rubber_plan_returns.${index}.return_count`}
													/>
												)}
											/>
										</Td>
										<Td>
											<Controller
												name={`rubber_plan.rubber_plan_returns.${index}.number`}
												control={control}
												render={({ field: { onChange, value }, fieldState: { error } }) => (
													<Input
														backgroundColor="transparent"
														tabIndex={5}
														isInvalid={!!error}
														onChange={onChange}
														value={convertNumberToNumberStringWithComma(
															String(value).replace(/[^0-9,.хx]/g, ''),
														)}
														name={`rubber_plan.rubber_plan_returns.${index}.number`}
													/>
												)}
											/>
										</Td>
										<Td></Td>
										<Td>
											<Controller
												name={`rubber_plan.rubber_plan_returns.${index}.fio`}
												control={control}
												render={({ field: { value, onChange }, fieldState: { error } }) => (
													<Select
														status={error?.message ? 'error' : ''}
														allowClear
														tabIndex={5}
														filterOption={(input, option) =>
															(option?.label ?? '')
																.toLowerCase()
																.includes(input.toLowerCase())
														}
														showSearch
														placeholder="Выберите ФИО"
														optionFilterProp="children"
														onChange={(e) => {
															onChange(e);
															setValue(
																`rubber_plan.rubber_plan_returns.${index}.fio`,
																e
																	? employees.find((el) => el.value === Number(e))
																			?.label
																	: '',
															);
														}}
														value={value}
														options={employees}
														style={{ width: '100%', whiteSpace: 'nowrap' }}
														className={styles['custom-select']}
													/>
												)}
											/>
										</Td>
									</Tr>
								))}
							</>
						)}
					</Table>
				</Flex>
			</Flex>
		</>
	);
};
